import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Finding, FindingsPayload, Freshness} from './types'

interface UseFindingsOptions {
  pullRequestBasePath: string
}

interface UseFindingsResult {
  findings: Finding[]
  freshness: Freshness
  modelsUrl: string | null
  isLoading: boolean
  error: string | null
  isNotFound: boolean
}

const DEFAULT_RETRY_AFTER_SECONDS = 10
const MIN_RETRY_AFTER_SECONDS = 1

const getRetryAfterMs = (
  response: Response | null,
  defaultRetryAfterSeconds: number = DEFAULT_RETRY_AFTER_SECONDS,
): number => {
  const retryAfterHeader = response?.headers.get('Retry-After')
  const parsedRetryAfter = retryAfterHeader === null ? Number.NaN : Number(retryAfterHeader)
  const retryAfterSeconds = Number.isFinite(parsedRetryAfter)
    ? Math.max(MIN_RETRY_AFTER_SECONDS, parsedRetryAfter)
    : defaultRetryAfterSeconds

  return retryAfterSeconds * 1000
}

interface FindingsQueryData {
  payload: FindingsPayload | null
  retryAfterMs: number
}

export function getRefetchInterval(queryData: FindingsQueryData | undefined): number | false {
  const freshness = queryData?.payload?.freshness
  if (freshness && freshness !== 'current') {
    return queryData?.retryAfterMs ?? DEFAULT_RETRY_AFTER_SECONDS * 1000
  }
  // Poll on 404 if the server sent Retry-After (findings not ready yet)
  if (queryData?.payload === null && queryData.retryAfterMs > 0) {
    return queryData.retryAfterMs
  }
  return false
}

export function useFindings({pullRequestBasePath}: UseFindingsOptions): UseFindingsResult {
  const {data, isLoading, error} = useQuery({
    queryKey: ['code-review-findings', pullRequestBasePath],
    queryFn: async (): Promise<FindingsQueryData> => {
      const response = await verifiedFetchJSON(`${pullRequestBasePath}/code_review_findings`)
      if (response.status === 404) {
        return {payload: null, retryAfterMs: getRetryAfterMs(response, 0)}
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch findings (${response.status})`)
      }
      const payload = (await response.json()) as FindingsPayload
      return {payload, retryAfterMs: getRetryAfterMs(response)}
    },
    refetchInterval: query => getRefetchInterval(query.state.data),
  })

  const payload = data?.payload

  return {
    findings: payload?.findings ?? [],
    freshness: payload?.freshness ?? 'pending',
    modelsUrl: payload?.modelsUrl ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch findings') : null,
    isNotFound: data?.payload === null && data?.retryAfterMs === 0 && !isLoading && !error,
  }
}
